import { PlatformLocation } from '@angular/common';
import { Component, Inject, Optional } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { IIncidentModalComponent } from '@max/incident/i-incident-modal.component';
import { toDateOrNull, toNumberOrNull, toStringOrNull } from '@max/incident/shared/data-helpers';
import { BaseComponent } from '@max/shared/base-components/base.component';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AccessControlType } from '../../foundation/models/enums/access-control-type.enum';
import { DocumentType } from '../../foundation/models/enums/document-type.enum';
import { MvdModalService } from '../../shared/services/modal/mvd-modal.service';
import { FraudAamvaModel } from '../models/fraud-aamva.model';
import { FraudDocumentModel } from '../models/fraud-document.model';
import { FraudAamvaIncidentService } from '../services/fraud-incident-aamva.service';
import { IncidentService } from '../services/incident.service';
import { IncidentDialogContext } from '../shared/incident-dialog-context.model';

@Component({
    selector: 'fraud-customer-aamva-modal',
    templateUrl: './fraud-customer-aamva-modal.html'
})
export class FraudCustomerAamvaModalComponent extends BaseComponent implements IIncidentModalComponent<FraudAamvaModel> {
    public form: UntypedFormGroup;
    public busy = false;
    public incidentNumberId: number;
    public documentTypes: any[] = new DocumentType().GetActive();
    public jurisdictions: any[] = [];
    public canEditNotes = false;
    private derivedCdl = false;
    private csrCdlOverride = false;
    private incidentTypeId: number = null;
    private disableCdlValidation = false;
    private accessControlType: AccessControlType = new AccessControlType();

    constructor(
        public dialog: MatDialogRef<FraudCustomerAamvaModalComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA)
        public data: IncidentDialogContext<FraudAamvaModel>,
        private fraudIncidentService: FraudAamvaIncidentService,
        public incidentService: IncidentService,
        private modalService: MvdModalService,
        location: PlatformLocation
    ) {
        super();
        location.onPopState(() => dialog.close(null));
        this.canEditNotes = this.auth.hasACSTransactionClaimEx(this.auth.acsTransactionClaim, this.accessControlType.AllowLawEnfNoteEdit.value) == true && this.data.isReadOnly == false;
    }

    public get documents(): UntypedFormArray {
        return this.form.get('documents') as UntypedFormArray;
    }

    public ngOnInit(): void {
        this.incidentNumberId = toNumberOrNull(this.data.incident.incidentNumberId);
    }

    public initLookups = (lookups: any): Observable<any> => {
        this.jurisdictions = lookups.vehicleJurisdictions || [];

        return this.incidentService.getCDLStatusOnDate(this.data.incident)
            .pipe(tap(r => this.evaluateDriverCdl(r)));
    };

    public closeModal = (result?: any): void => {
        this.dialog.close(result);
    };

    public populateModel = (form: UntypedFormGroup, model: FraudAamvaModel): void => {
        model.investigatorName = toStringOrNull(form.get('investigatorName').value);
        model.investigatorPhoneNumber = toStringOrNull(form.get('investigatorPhoneNumber').value);
        model.commercialMotorVehicleIndicator = true == form.get('commercialMotorVehicleIndicator').value;
        model.hazardousMaterialIndicator = true == form.get('hazardousMaterialIndicator').value;
        model.passengerIndicator = true == form.get('passengerIndicator').value;
        model.cdlHolderIndicator = true == form.get('isCDLHolder').value;
        model.csrCdlOverride = this.csrCdlOverride;
        model.driverLicenseNumber = toStringOrNull(form.get('driverLicenseNumber').value);
        model.driverLicenseJurisdictionId = toNumberOrNull(form.get('driverLicenseJurisdictionId').value);
        model.fraudDocuments = [];

        for (let d of this.documents.controls) {
            let documentModel = new FraudDocumentModel();
            documentModel.id = toNumberOrNull(d.get('documentId').value);
            documentModel.documentTypeId = toNumberOrNull(d.get('documentTypeId').value);
            documentModel.documentNumber = toStringOrNull(d.get('documentNumber').value);
            documentModel.documentIssueDate = toDateOrNull(d.get('documentIssueDate').value);
            documentModel.deleted = true == d.get('deleted').value;
            model.fraudDocuments.push(documentModel);
        }
    };

    public formInitialized = (form: UntypedFormGroup): void => {
        this.form = form;
        form.addControl('investigatorName', new UntypedFormControl(''));
        form.addControl('investigatorPhoneNumber', new UntypedFormControl(''));
        form.addControl('commercialMotorVehicleIndicator', new UntypedFormControl(false));
        form.addControl('hazardousMaterialIndicator', new UntypedFormControl(false));
        form.addControl('passengerIndicator', new UntypedFormControl(false));
        form.addControl('isCDLHolder', new UntypedFormControl(false));
        form.addControl('driverLicenseNumber', new UntypedFormControl(''));
        form.addControl('driverLicenseJurisdictionId', new UntypedFormControl(null));
        form.addControl('documents', new UntypedFormArray([]));
        form.get('isCDLHolder').valueChanges.subscribe(v => this.isCdlHolderChanged(v));
        form.get('occurrenceDate').valueChanges.subscribe(_ => this.occurrenceDateChanged());

        if (null != this.incidentTypeId) {
            this.updateFormValidationRules(form, this.incidentTypeId);
        }
    };

    public updateFormValidationRules = (form: UntypedFormGroup, incidentTypeId: number): void => {
        this.incidentTypeId = incidentTypeId;
    };

    public updateData = (form: UntypedFormGroup, incident: FraudAamvaModel): void => {
        form.get('investigatorName').setValue(toStringOrNull(incident.investigatorName));
        form.get('investigatorPhoneNumber').setValue(toStringOrNull(incident.investigatorPhoneNumber));
        form.get('commercialMotorVehicleIndicator').setValue(true == incident.commercialMotorVehicleIndicator);
        form.get('hazardousMaterialIndicator').setValue(true == incident.hazardousMaterialIndicator);
        form.get('passengerIndicator').setValue(true == incident.passengerIndicator);
        form.get('driverLicenseNumber').setValue(toStringOrNull(incident.driverLicenseNumber));
        form.get('driverLicenseJurisdictionId').setValue(toNumberOrNull(incident.driverLicenseJurisdictionId));
        this.setCdlHolderValue(true == incident.cdlHolderIndicator);
        let documents = form.get('documents') as UntypedFormArray;
        documents.clear();

        if (null != incident.fraudDocuments) {
            for (let d of incident.fraudDocuments) {
                let group = this.buildDocumentFormElement(d);
                documents.push(group);
            }
        }
    };

    public getIncident = (incidentNumberId: number): Observable<FraudAamvaModel> => this.fraudIncidentService.getIncident(incidentNumberId);

    public save = (model: FraudAamvaModel): Observable<any> => this.fraudIncidentService.saveIncident(model);

    public loadComplete = (): void => {
    };

    public addDocument = (): void => {
        let group = this.buildDocumentFormElement();
        this.documents.push(group);
    };

    public deleteDocument = (document: UntypedFormGroup): void => {
        document.get('deleted').setValue(true);
    };

    private buildDocumentFormElement = (document?: FraudDocumentModel): UntypedFormGroup => {
        let group = new UntypedFormGroup({
            documentId: new UntypedFormControl(null),
            documentTypeId: new UntypedFormControl(null, Validators.required),
            documentNumber: new UntypedFormControl(''),
            documentIssueDate: new UntypedFormControl(null),
            deleted: new UntypedFormControl(false)
        });

        let documentTypeId = null;

        if (null != document) {
            documentTypeId = toNumberOrNull(document.documentTypeId);

            group.get('documentId').setValue(toNumberOrNull(document.id));
            group.get('documentTypeId').setValue(documentTypeId);
            group.get('documentNumber').setValue(toStringOrNull(document.documentNumber));
            group.get('documentIssueDate').setValue(toDateOrNull(document.documentIssueDate));
        }

        if (null == documentTypeId) {
            group.get('documentNumber').disable();
        }

        group.get('documentTypeId').valueChanges.subscribe(v => {
            if (null == v) {
                group.get('documentNumber').disable();
            } else {
                group.get('documentNumber').enable();
            }
        });

        return group;
    };

    private evaluateDriverCdl = (checkCdl: any): void => {
        this.derivedCdl = true == checkCdl.result;
        this.setCdlHolderValue(this.derivedCdl);
    };

    private setCdlHolderValue = (value: boolean): void => {
        this.disableCdlValidation = true;

        try {
            this.form.get('isCDLHolder').setValue(value);
        } finally {
            this.disableCdlValidation = false;
        }
    };

    private isCdlHolderChanged = (value: boolean): void => {
        if (true == this.disableCdlValidation) {
            return;
        }

        this.csrCdlOverride = false;

        if ((null != this.derivedCdl) && (value != this.derivedCdl)) {
            this.modalService.confirm()
                .title('CDL Holder Confirmation')
                .body('MAX has derived a different value for CDL Holder indicator than what you have selected, do you want to continue?')
                .okBtn('Yes')
                .cancelBtn('No')
                .open()
                .afterClosed()
                .subscribe(r => {
                    if (false == r) {
                        this.form.get('isCDLHolder').setValue(true == this.derivedCdl);
                    } else {
                        this.csrCdlOverride = true;
                    }
                });
        }
    };

    private occurrenceDateChanged = (): void => {
        if (true == this.busy) {
            return;
        }

        if (null == toNumberOrNull(this.data.incident.incidentNumberId) && null != toDateOrNull(this.data.incident.occurrenceDate)) {
            this.busy = true;
            this.incidentService.getCDLStatusOnDate(this.data.incident)
                .subscribe(r => {
                    this.setCdlHolderValue(true == r.result);
                    this.derivedCdl = true == r.result;
                },
                e => this.handleValidationResult(e),
                () => this.busy = false);
        }
    };
}
